/**
 * Post-download confirmation instrumentation — TES-1577, amended for
 * Plate & Hairline.
 *
 * TES-1577's original finding: the sound-card and mobile-player download
 * paths confirmed with hand-rolled toasts and NO instrumentation, so
 * `download_confirm_shown` covered only ~11% of downloads and the /guide
 * attribution (`guide_visited_post_download`) never armed for the majority
 * path. Main's fix routed both through one toast helper.
 *
 * The Plate & Hairline system then refused toasts outright (§16, AMENDED —
 * see the no-restricted-imports rule on `sonner`): each surface confirms
 * inline (the card's own completion state, the mini-player's message row +
 * live-region announce). What survives from TES-1577 is the invariant that
 * MATTERS: the attribution write and the impression event fire exactly when
 * a confirmation becomes visible, from one helper, so they can't drift
 * apart from the UI again. Rendering is the caller's job; recording is ours.
 */

import { markDownloadCompleted } from "@/lib/download-attribution";
import {
  trackDownloadConfirmShown,
  trackReferralLinkGenerated,
  trackReferralLinkCopied,
} from "@/lib/tracking";
import { buildSurfaceShareUrl } from "@/lib/social-share-copy";

/**
 * Placement dimension on `download_confirm_shown`. The `*_toast` values are
 * retired with the toasts themselves but remain in lib/ga4.ts's event union
 * so historical dashboards keep their meaning; new callers report an
 * `*_inline` surface.
 */
export type PostDownloadSurface = "sound_card_inline" | "mobile_player_inline";

export interface PostDownloadConfirmationOptions {
  soundId: string;
  soundTitle: string;
  /** Prefer the slug — UUID paths 308-redirect and unfurl as non-canonical. */
  soundSlug?: string | null;
  /** `download_completed`'s source, carried into the /guide attribution. */
  source: string;
  surface: PostDownloadSurface;
}

/**
 * Records download attribution and reports the confirmation impression.
 * Call once from the download's `onSuccess`, at the moment the caller's own
 * confirmation UI becomes visible.
 */
export function recordPostDownloadConfirmation({
  soundId,
  soundTitle,
  soundSlug,
  source,
  surface,
}: PostDownloadConfirmationOptions) {
  // Arms `guide_visited_post_download` for the next /guide landing this session.
  markDownloadCompleted({ sound_id: soundId, sound_title: soundTitle, source });

  // The caller invokes this exactly when its confirmation UI becomes visible,
  // so the call == the impression; there is nothing to debounce.
  trackDownloadConfirmShown({
    sound_id: soundId,
    sound_title: soundTitle,
    surface,
  });
}
